import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Textarea,
  tokens,
} from "@fluentui/react-components";
import { AddRegular, TreeDeciduousRegular } from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApiError } from "../api/client";
import {
  type Category,
  commerce,
  MAX_CATEGORY_DEPTH,
  type SaveCategory,
  type SerialisationPolicy,
} from "../api/commerce";
import { ConfirmDialog } from "../shell/ConfirmDialog";
import { baeTypography } from "../theme/baeTheme";
import { EmptyState, Page, PageHeader } from "../ui/PageHeader";
import { Card } from "../ui/Surface";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  list: { display: "grid", gap: tokens.spacingVerticalXS, minWidth: 0 },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  rowBody: { display: "grid", gap: "2px", flexGrow: 1, minWidth: 0 },
  dim: { opacity: 0.55 },
  muted: { color: tokens.colorNeutralForeground3 },
  code: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "12px" },
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
    gap: tokens.spacingHorizontalM,
  },
  rowActions: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
});

const POLICIES: { value: SerialisationPolicy; label: string; hint: string }[] = [
  {
    value: "Required",
    label: "Label every piece",
    hint: "Each piece gets its own serial and barcode.",
  },
  {
    value: "None",
    label: "Count only",
    hint: "Stock is a number. Nothing is labelled individually.",
  },
  { value: "Optional", label: "Label when asked", hint: "Decided per delivery." },
];

const policyLabel = (value: string) => POLICIES.find((p) => p.value === value)?.label ?? value;

/** Derives a code from the name, so nobody has to invent one. Set once, then left alone. */
const codeFrom = (name: string) =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 24);

type Draft = SaveCategory & { id: string | null };

const emptyDraft = (parentCategoryId: string | null, sortOrder: number): Draft => ({
  id: null,
  code: "",
  name: "",
  description: null,
  parentCategoryId,
  serialisationPolicy: "Required",
  sortOrder,
});

const draftFrom = (category: Category): Draft => ({
  id: category.id,
  code: category.code,
  name: category.name,
  description: category.description,
  parentCategoryId: category.parentCategoryId,
  serialisationPolicy: category.serialisationPolicy,
  sortOrder: category.sortOrder,
});

/**
 * The tenant's category tree.
 *
 * This is the first screen an owner uses, because everything else hangs off it: which fields a
 * piece is asked about, and whether its units get individual labels. The API returns the tree
 * already ordered and already carrying each node's depth, so this file draws what it is given
 * rather than working out the shape for itself (ADR-0009).
 */
export function CategoriesPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);

  const categories = useQuery({ queryKey: ["categories"], queryFn: commerce.categories });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["categories"] });
    // The form's questions are scoped by category, so they can change with the tree.
    void queryClient.invalidateQueries({ queryKey: ["attributes"] });
  };

  const save = useMutation({
    mutationFn: ({ id, ...body }: Draft) =>
      id
        ? commerce.updateCategory(id, body)
        : commerce.createCategory({ ...body, code: body.code || codeFrom(body.name) }),
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: (category: Category) => commerce.setCategoryActive(category.id, !category.isActive),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (category: Category) => commerce.deleteCategory(category.id),
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  const rows = categories.data ?? [];

  // Only categories that could still take a child are offered as a parent. Anything at the
  // ceiling is left out rather than offered and then refused by the API.
  const possibleParents = useMemo(() => rows.filter((c) => c.depth < MAX_CATEGORY_DEPTH), [rows]);

  const nextSortOrder = (parentId: string | null) =>
    Math.max(0, ...rows.filter((c) => c.parentCategoryId === parentId).map((c) => c.sortOrder)) +
    10;

  const error = save.error ?? toggle.error;

  // Named before the click rather than after it. The API is still the authority and refuses in the
  // same order, but a dialog that already knows what is in the way saves a round trip to find out
  // — and saying "6 products" when the real blocker is two sub-categories would be worse than
  // saying nothing.
  const blockers = pendingDelete
    ? [
        rows.filter((c) => c.parentCategoryId === pendingDelete.id).length > 0 &&
          `${rows.filter((c) => c.parentCategoryId === pendingDelete.id).length} sub-categories sit inside it`,
        pendingDelete.productCount > 0 && `${pendingDelete.productCount} product(s) are filed here`,
      ].filter((entry): entry is string => typeof entry === "string")
    : [];

  return (
    <Page width="medium">
      <PageHeader
        title="Categories"
        subtitle={`What you sell, arranged the way you think about it — up to ${MAX_CATEGORY_DEPTH} levels, and only as many as you want. Kurtis can stand on its own, or hold Long and Short beneath it. Set this up first: the questions asked when adding a piece are attached to categories.`}
        actions={
          !draft ? (
            <Button
              appearance="primary"
              icon={<AddRegular />}
              onClick={() => setDraft(emptyDraft(null, nextSortOrder(null)))}
            >
              Add a category
            </Button>
          ) : undefined
        }
      />

      {error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {draft ? (
        <CategoryEditor
          draft={draft}
          parents={possibleParents}
          busy={save.isPending}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSave={() => save.mutate(draft)}
        />
      ) : null}

      {categories.isLoading ? (
        <Spinner label="Loading categories" />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<TreeDeciduousRegular />}
            title="No categories yet"
            action={
              <Button
                appearance="primary"
                icon={<AddRegular />}
                onClick={() => setDraft(emptyDraft(null, nextSortOrder(null)))}
              >
                Add the first one
              </Button>
            }
          >
            Start with the broad ones — Apparel, Accessories — then add what sits inside them.
          </EmptyState>
        </Card>
      ) : (
        <div className={styles.list}>
          {rows.map((category) => (
            <div
              key={category.id}
              className={`${styles.row} ${category.isActive ? "" : styles.dim}`}
              // Indented by the depth the API reported, so the nesting is visible at a glance.
              style={{ marginInlineStart: `${(category.depth - 1) * 1.5}rem` }}
            >
              <div className={styles.rowBody}>
                <Body1Strong>{category.name}</Body1Strong>
                <Caption1 className={styles.muted}>
                  <span className={styles.code}>{category.code}</span> ·{" "}
                  {policyLabel(category.serialisationPolicy)}
                  {category.productCount > 0 ? ` · ${category.productCount} products` : ""}
                </Caption1>
              </div>

              {category.depth < MAX_CATEGORY_DEPTH && (
                <Button
                  appearance="subtle"
                  onClick={() => setDraft(emptyDraft(category.id, nextSortOrder(category.id)))}
                >
                  Add under
                </Button>
              )}

              {!category.isActive && (
                <Badge appearance="tint" size="small">
                  Hidden
                </Badge>
              )}

              <div className={styles.rowActions}>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => setDraft(draftFrom(category))}
                >
                  Edit
                </Button>
                <Button size="small" appearance="subtle" onClick={() => toggle.mutate(category)}>
                  {category.isActive ? "Hide" : "Show"}
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => {
                    remove.reset();
                    setPendingDelete(category);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Caption1 className={styles.muted}>
        Hiding a category hides everything under it and keeps what is already filed there. Deleting
        works only on an empty one — nothing inside it, no products, and no fields pointing at it —
        so a branch cannot go in a single click.
      </Caption1>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.name ?? ""}?`}
        confirmLabel="Delete it"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
      >
        <Caption1>
          {blockers.length > 0
            ? `${blockers.join(" and ")} — so this will be refused. Clear that first, or hide the category instead, which takes it off the form and leaves everything readable.`
            : "This only works while the category is empty. If anything is filed under it, nothing is deleted and you will be told what is in the way."}
        </Caption1>
      </ConfirmDialog>
    </Page>
  );
}

function CategoryEditor({
  draft,
  parents,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft;
  parents: Category[];
  busy: boolean;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const styles = useStyles();
  const isNew = draft.id === null;

  // A category cannot be its own parent, nor sit inside its own branch. The API refuses both; the
  // dropdown simply does not offer them.
  const options = parents.filter((p) => p.id !== draft.id && !p.path.includes(draft.name));

  const parentLabel =
    options.find((p) => p.id === draft.parentCategoryId)?.path.join(" / ") ?? "Top level";

  return (
    <div className={styles.panel}>
      <Body1Strong>{isNew ? "New category" : `Editing ${draft.name}`}</Body1Strong>

      <div className={styles.pair}>
        <Field label="Name" required hint="What the team sees when adding a piece.">
          <Input
            value={draft.name}
            placeholder="Kurtis"
            onChange={(_, d) =>
              onChange({ ...draft, name: d.value, code: isNew ? codeFrom(d.value) : draft.code })
            }
          />
        </Field>

        <Field
          label="Sits under"
          hint={`Leave at top level for a main category. ${MAX_CATEGORY_DEPTH} levels at most.`}
        >
          <Dropdown
            value={parentLabel}
            selectedOptions={[draft.parentCategoryId ?? "root"]}
            onOptionSelect={(_, data) =>
              onChange({
                ...draft,
                parentCategoryId: data.optionValue === "root" ? null : (data.optionValue ?? null),
              })
            }
          >
            <Option value="root" text="Top level">
              Top level
            </Option>
            {options.map((parent) => (
              <Option key={parent.id} value={parent.id} text={parent.path.join(" / ")}>
                {parent.path.join(" / ")}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      <Field
        label="How is stock tracked?"
        hint={POLICIES.find((p) => p.value === draft.serialisationPolicy)?.hint}
      >
        <Dropdown
          value={policyLabel(draft.serialisationPolicy)}
          selectedOptions={[draft.serialisationPolicy]}
          onOptionSelect={(_, data) =>
            onChange({ ...draft, serialisationPolicy: data.optionValue as SerialisationPolicy })
          }
        >
          {POLICIES.map((policy) => (
            <Option key={policy.value} value={policy.value} text={policy.label}>
              {policy.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <Field label="Note" hint="Optional. For whoever maintains this list.">
        <Textarea
          value={draft.description ?? ""}
          rows={2}
          onChange={(_, d) => onChange({ ...draft, description: d.value || null })}
        />
      </Field>

      <div className={styles.actions}>
        <Button appearance="primary" disabled={draft.name.trim() === "" || busy} onClick={onSave}>
          {busy ? <Spinner size="tiny" /> : "Save category"}
        </Button>
        <Button appearance="subtle" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
